<template>
  <v-container :class="timerView ? 'fill-height' : ''">
    <v-card v-if="!timerView" class="rounded-lg">
      <v-timeline align-top dense>
        <v-timeline-item hide-dot>
          <div class="d-flex align-center flex-wrap">
            <span
              class="text-h5 font-weight-bold"
              style="word-break: normal; line-height: 1.2"
              v-text="tournament ? tournament.name : ''"
            ></span>
            <span
              class="text-subtitle-1 grey--text text--darken-1 ml-2"
              v-text="'Schedule'"
            ></span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="schedules.length"
              small
              text
              color="primary"
              class="text-none"
              @click="timerView = true"
            >
              <v-icon small left v-text="'mdi-timer-outline'"></v-icon>
              <span v-text="'Timer View'"></span>
            </v-btn>
          </div>
        </v-timeline-item>
        <div v-for="(schedules, i) in scheduleList" :key="i">
          <v-timeline-item hide-dot>
            <strong class="secondary--text" v-text="schedules[0].date"></strong>
          </v-timeline-item>
          <v-timeline-item
            v-for="schedule in schedules"
            :key="schedule.id"
            color="primary"
            small
          >
            <v-row>
              <v-col cols="3">
                <strong v-text="schedule.start + ' - ' + schedule.end"></strong>
              </v-col>
              <v-col>
                <strong v-text="schedule.title"></strong>
                <div class="text-caption" v-text="schedule.description"></div>
              </v-col>
            </v-row>
          </v-timeline-item>
        </div>
      </v-timeline>
    </v-card>

    <v-card v-else class="fill-height rounded-lg d-flex flex-column" outlined>
      <v-card-title class="py-1">
        <v-btn
          small
          text
          color="primary"
          class="text-none"
          @click="timerView = false"
        >
          <v-icon small left v-text="'mdi-calendar-clock'"></v-icon>
          <span v-text="'Schedule'"></span>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          small
          text
          color="primary"
          class="text-none"
          @click="toggleFullscreen"
        >
          <v-icon
            small
            left
            v-text="fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"
          ></v-icon>
          <span
            v-show="!$vuetify.breakpoint.xs"
            v-text="fullscreen ? 'Exit fullscreen' : 'Fullscreen'"
          ></span>
        </v-btn>
      </v-card-title>

      <div class="flex-grow-1 d-flex align-stretch">
        <div
          class="flex-grow-1 d-flex flex-column align-center justify-center text-center px-2 px-sm-4 pb-4"
          style="min-width: 0"
        >
          <div
            class="font-weight-bold mb-2"
            :class="$vuetify.breakpoint.smAndDown ? 'text-h6' : 'text-h4'"
            style="line-height: 1.2; word-break: normal"
            v-text="tournament ? tournament.name : ''"
          ></div>

          <span
            v-if="!displaySchedule"
            class="grey--text"
            v-text="'No schedule available'"
          ></span>

          <template v-else>
            <div
              class="text-overline grey--text text--darken-1"
              v-text="phaseLabel"
            ></div>
            <div
              class="display-1 font-weight-bold"
              v-text="displaySchedule.title"
            ></div>
            <div
              v-if="displaySchedule.description"
              class="subtitle-1 mt-2"
              v-text="displaySchedule.description"
            ></div>
            <div
              class="text-caption grey--text text--darken-1 mt-1"
              v-text="
                displaySchedule.dateShort +
                ' • ' +
                displaySchedule.start +
                ' - ' +
                displaySchedule.end
              "
            ></div>

            <flip-countdown
              v-if="!isTimeOut"
              :key="deadline"
              :deadline="deadline"
              :showDays="false"
              :countdownSize="countdownSize"
              :labelSize="labelSize"
              class="mt-6"
            ></flip-countdown>

            <div
              v-else
              class="d-flex justify-center mt-6"
              style="align-self: stretch"
            >
              <v-card
                class="mx-2 mx-sm-10 overflow-auto"
                :class="countdownSizePx <= 70 ? 'rounded-lg' : 'rounded-xl'"
                color="#222"
                dark
                :height="countdownSizePx * 1.5"
                :width="countdownSizePx * 6.4"
                style="max-width: 100%"
              >
                <v-card-title class="fill-height">
                  <v-spacer></v-spacer>
                  <v-img
                    alt="Bridge Pocket Logo"
                    class="shrink"
                    contain
                    :src="require('../assets/logo-512px.png')"
                    transition="scale-transition"
                    :width="countdownSizePx"
                  />
                  <div
                    class="font-weight-bold"
                    :class="blinkOn ? 'yellow--text' : 'red--text'"
                    :style="'font-size:' + countdownSizePx + 'px'"
                  >
                    &nbsp;Time Out
                  </div>
                  <v-spacer></v-spacer>
                </v-card-title>
              </v-card>
            </div>

            <div
              v-if="nextSchedule"
              class="text-caption grey--text text--darken-1 mt-4"
            >
              <span class="font-weight-medium" v-text="'Next:'"></span>
              <span
                v-text="' ' + nextSchedule.title + ' • ' + nextScheduleTime"
              ></span>
            </div>
          </template>
        </div>

        <div
          class="d-flex align-center flex-grow-0 flex-shrink-0 pr-1"
          style="width: 56px"
        >
          <v-slider
            v-model="countdownSlider"
            min="40"
            :max="maxCountdownSize"
            step="5"
            vertical
            hide-details
            class="fill-height"
          ></v-slider>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { db } from "@/App.vue";
import { ref, onValue } from "firebase/database";
import FlipCountdown from "vue2-flip-countdown";
import {
  nextSchedule as findNextSchedule,
  parseSchedules,
  pickDisplaySchedule,
  SCHEDULE_DEADLINE_FORMAT,
} from "@/utils/schedule";

const deadlineFormat = SCHEDULE_DEADLINE_FORMAT;
const countdownSizeStorageKey = "bridge-pocket.schedule-timer.countdown-size";

export default {
  name: "TournamentScheduleVue",

  components: { FlipCountdown },

  data: () => ({
    tournament: null,
    schedules: [],
    timerView: false,
    fullscreen: false,
    countdownSlider: null,
    skipSizeSave: false,
    now: Date.now(),
    interval: null,
    dateFormat: "dddd, MMM DD, YYYY",
  }),

  created() {
    //Load Tournament
    const tournamentRef = ref(db, "tournaments/" + this.$route.params.id);
    onValue(tournamentRef, (snapshot) => {
      this.tournament = snapshot.val();
    });

    //Load Schedules
    const scheduleRef = ref(db, "tournamentSchedule/" + this.$route.params.id);
    onValue(scheduleRef, (snapshot) => {
      this.schedules = parseSchedules(snapshot, this.dateFormat);
    });

    this.interval = setInterval(() => {
      this.now = Date.now();
    }, 500);

    this.skipSizeSave = true;
    this.countdownSlider = this.restoreCountdownSize();
    this.$nextTick(() => {
      this.skipSizeSave = false;
    });

    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  },

  beforeDestroy() {
    clearInterval(this.interval);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    if (this.fullscreen) this.applyPresentation(false);
    if (document.fullscreenElement && document.exitFullscreen)
      document.exitFullscreen().catch(() => {});
  },

  watch: {
    timerView(value) {
      if (!value) this.exitFullscreen();
    },
    maxCountdownSize(value) {
      if (this.countdownSlider <= value) return;
      this.skipSizeSave = true;
      this.countdownSlider = value;
      this.$nextTick(() => {
        this.skipSizeSave = false;
      });
    },
    countdownSlider(value) {
      if (this.skipSizeSave || !value) return;
      try {
        window.localStorage.setItem(countdownSizeStorageKey, String(value));
      } catch (e) {
        // Storage can be blocked (private mode); the slider still works.
      }
    },
  },

  computed: {
    scheduleList() {
      const list = [];
      this.schedules.forEach((schedule) => {
        const last = list[list.length - 1];
        if (last && last[0].dateKey === schedule.dateKey) last.push(schedule);
        else list.push([schedule]);
      });
      return list;
    },
    displayState() {
      return pickDisplaySchedule(this.schedules, this.now);
    },
    displaySchedule() {
      return this.displayState ? this.displayState.schedule : null;
    },
    isUpcoming() {
      return this.displayState ? this.displayState.isUpcoming : false;
    },
    isTimeOut() {
      return this.displayState ? this.displayState.isTimeOut : false;
    },
    nextSchedule() {
      return findNextSchedule(this.schedules, this.displaySchedule);
    },
    nextScheduleTime() {
      if (!this.nextSchedule) return "";
      const sameDay =
        this.displaySchedule &&
        this.displaySchedule.dateKey === this.nextSchedule.dateKey;
      return sameDay
        ? this.nextSchedule.start
        : this.nextSchedule.dateShort + ', ' + this.nextSchedule.start;
    },
    deadline() {
      if (!this.displaySchedule) return moment().format(deadlineFormat);
      const target = this.isUpcoming
        ? this.displaySchedule.startMoment
        : this.displaySchedule.endMoment;
      return target.format(deadlineFormat);
    },
    phaseLabel() {
      if (!this.displaySchedule) return "";
      if (this.isTimeOut) return "Time Out";
      return this.isUpcoming ? "Starts in" : "Time remaining";
    },
    defaultCountdownSize() {
      if (this.$vuetify.breakpoint.xs) return 54;
      if (this.$vuetify.breakpoint.sm) return 90;
      if (this.$vuetify.breakpoint.md) return 120;
      return 140;
    },
    countdownSizePx() {
      return this.countdownSlider || this.defaultCountdownSize;
    },
    maxCountdownSize() {
      if (this.$vuetify.breakpoint.xs) return 90;
      if (this.$vuetify.breakpoint.sm) return 140;
      return 200;
    },
    countdownSize() {
      return this.countdownSizePx + "px";
    },
    labelSize() {
      return this.countdownSizePx / 5 + "px";
    },
    blinkOn() {
      return Math.floor(this.now / 500) % 2 == 0;
    },
  },

  methods: {
    restoreCountdownSize() {
      let stored = NaN;
      try {
        stored = parseInt(
          window.localStorage.getItem(countdownSizeStorageKey),
          10
        );
      } catch (e) {
        stored = NaN;
      }
      if (isNaN(stored)) return this.defaultCountdownSize;
      return Math.min(Math.max(stored, 40), this.maxCountdownSize);
    },
    applyPresentation(on) {
      this.fullscreen = on;
      document.documentElement.classList.toggle("presentation-mode", on);
    },
    async enterFullscreen() {
      this.applyPresentation(true);
      const el = document.documentElement;
      if (!document.fullscreenElement && el.requestFullscreen) {
        try {
          await el.requestFullscreen();
        } catch (e) {
          // Browser fullscreen can be refused; the presentation class still
          // hides the navigation chrome, so keep that state.
        }
      }
    },
    async exitFullscreen() {
      if (document.fullscreenElement && document.exitFullscreen) {
        try {
          await document.exitFullscreen();
        } catch (e) {
          // Ignore: the presentation class is removed below anyway.
        }
      }
      this.applyPresentation(false);
    },
    toggleFullscreen() {
      if (this.fullscreen) this.exitFullscreen();
      else this.enterFullscreen();
    },
    onFullscreenChange() {
      if (!document.fullscreenElement && this.fullscreen)
        this.applyPresentation(false);
    },
  },
};
</script>

<style>
/* Presentation mode hides the app shell so the timer can fill a projector. */
.presentation-mode .v-app-bar,
.presentation-mode .v-navigation-drawer,
.presentation-mode .v-footer,
.presentation-mode .v-breadcrumbs,
.presentation-mode .v-slider {
  display: none !important;
}

.presentation-mode .v-main {
  padding: 0 !important;
}

/* Hide the layout's own header row (menu button, breadcrumbs, scroll speed),
   whose wrapper columns would otherwise keep their height. */
.presentation-mode .v-main .row > .col-auto.pb-0,
.presentation-mode .v-main .row > .col.pb-0,
.presentation-mode .v-main .row > .col-3.pb-0 {
  display: none !important;
}
</style>
